import type React from 'react'
import { useEffect, useRef, useState } from 'react'

interface SwipeState {
  startX: number
  startY: number
  currentX: number
  currentY: number
  isDragging: boolean
  direction: 'left' | 'right' | 'up' | 'down' | null
  offsetX: number
  offsetY: number
}

interface UseSwipeGestureOptions {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
  threshold?: number // Minimum distance to trigger swipe
  velocityThreshold?: number // Minimum velocity to trigger swipe
}

interface UseSwipeGestureReturn {
  // Ref to attach to the swipeable element for direct DOM manipulation
  swipeRef: React.RefObject<HTMLDivElement | null>
  // Whether a drag is currently in progress
  isDragging: boolean
}

export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  velocityThreshold = 0.3,
}: UseSwipeGestureOptions): UseSwipeGestureReturn {
  // Ref to the swipeable element for direct DOM manipulation
  const swipeRef = useRef<HTMLDivElement | null>(null)
  
  // React state for isDragging (for consumers that need reactive updates)
  const [isDragging, setIsDragging] = useState(false)
  
  // Use refs to track state without triggering re-renders during drag
  const stateRef = useRef<SwipeState>({
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isDragging: false,
    direction: null,
    offsetX: 0,
    offsetY: 0,
  })
  
  const startTimeRef = useRef<number>(0)
  const lockedAxisRef = useRef<'horizontal' | 'vertical' | null>(null)
  const rafRef = useRef<number | null>(null)
  const resetTimeoutRef = useRef<number | null>(null)
  
  // Store callbacks in refs to avoid re-attaching listeners
  const callbacksRef = useRef({ onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown })
  useEffect(() => {
    callbacksRef.current = { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown }
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown])

  // Attach native event listeners with { passive: false } to allow preventDefault
  useEffect(() => {
    const element = swipeRef.current
    if (!element) return
    
    // Get the inner carousel div (first child)
    const innerEl = element.firstElementChild as HTMLElement | null
    
    // The transition we want when NOT dragging - 0.6s spring easing
    const TRANSITION = 'transform 0.6s cubic-bezier(0.32, 0.72, 0, 1)'
    
    const applyTransform = (offsetX: number, dragging: boolean) => {
      element.style.setProperty('--swipe-offset', `${offsetX}px`)
      // Directly control transition on the inner element
      if (innerEl) {
        // Must set explicit value, empty string doesn't restore inline styles
        innerEl.style.transition = dragging ? 'none' : TRANSITION
      }
    }
    
    const handleTouchStart = (e: TouchEvent) => {
      // Ignore multi-touch (pinch-zoom, etc.)
      if (e.touches.length !== 1) return
      
      // Cancel any pending animation frame
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      
      const touch = e.touches[0]
      stateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        currentX: touch.clientX,
        currentY: touch.clientY,
        isDragging: true,
        direction: null,
        offsetX: 0,
        offsetY: 0,
      }
      startTimeRef.current = Date.now()
      lockedAxisRef.current = null
      
      // Update React state
      setIsDragging(true)
      
      // Apply initial transform (no offset, but mark as dragging)
      applyTransform(0, true)
    }
    
    const handleTouchMove = (e: TouchEvent) => {
      // Ignore multi-touch (pinch-zoom, etc.)
      if (e.touches.length !== 1) return
      if (!stateRef.current.isDragging) return
      
      const touch = e.touches[0]
      const deltaX = touch.clientX - stateRef.current.startX
      const deltaY = touch.clientY - stateRef.current.startY
      
      // Lock to axis after moving 10px
      if (!lockedAxisRef.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
        lockedAxisRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
      }
      
      stateRef.current.currentX = touch.clientX
      stateRef.current.currentY = touch.clientY
      
      // Determine direction and update offsets
      if (lockedAxisRef.current === 'horizontal') {
        // Prevent Safari from scrolling/rubber-banding during horizontal swipe
        // This works because we registered with { passive: false }
        e.preventDefault()
        
        stateRef.current.direction = deltaX > 0 ? 'right' : 'left'
        stateRef.current.offsetX = deltaX
        stateRef.current.offsetY = 0
        
        // Apply transform directly via RAF (batched, no React re-render)
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null
            applyTransform(stateRef.current.offsetX, true)
          })
        }
      } else if (lockedAxisRef.current === 'vertical') {
        stateRef.current.direction = deltaY > 0 ? 'down' : 'up'
        stateRef.current.offsetX = 0
        stateRef.current.offsetY = deltaY
      }
    }
    
    const handleTouchEnd = () => {
      if (!stateRef.current.isDragging) return
      
      // Cancel any pending animation frame
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      
      const deltaX = stateRef.current.currentX - stateRef.current.startX
      const deltaY = stateRef.current.currentY - stateRef.current.startY
      const duration = Date.now() - startTimeRef.current
      
      // Calculate velocity (px/ms) - guard against division by zero
      const velocityX = duration > 0 ? Math.abs(deltaX) / duration : 0
      const velocityY = duration > 0 ? Math.abs(deltaY) / duration : 0
      
      // Determine if swipe threshold was met
      const isHorizontalSwipe = lockedAxisRef.current === 'horizontal'
      const isVerticalSwipe = lockedAxisRef.current === 'vertical'
      
      const callbacks = callbacksRef.current
      let swipeSucceeded = false
      
      if (isHorizontalSwipe) {
        const meetsThreshold = Math.abs(deltaX) > threshold || velocityX > velocityThreshold
        if (meetsThreshold) {
          swipeSucceeded = true
          if (deltaX > 0) {
            callbacks.onSwipeRight?.()
          } else {
            callbacks.onSwipeLeft?.()
          }
        }
      } else if (isVerticalSwipe) {
        const meetsThreshold = Math.abs(deltaY) > threshold || velocityY > velocityThreshold
        if (meetsThreshold) {
          swipeSucceeded = true
          if (deltaY > 0) {
            callbacks.onSwipeDown?.()
          } else {
            callbacks.onSwipeUp?.()
          }
        }
      }
      
      // Reset internal state
      stateRef.current.isDragging = false
      stateRef.current.direction = null
      stateRef.current.offsetX = 0
      stateRef.current.offsetY = 0
      lockedAxisRef.current = null
      
      // Update React state
      setIsDragging(false)
      
      // Re-enable CSS transition on inner element with explicit value
      const innerEl = element.firstElementChild as HTMLElement | null
      if (innerEl) {
        innerEl.style.transition = 'transform 0.6s cubic-bezier(0.32, 0.72, 0, 1)'
      }
      
      // Always reset offset to 0
      // For successful swipes: the callback changes activeIndex, which changes baseOffsetPercent
      // For failed swipes: just snaps back to original position
      // Small delay ensures React has processed the state update
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current)
      }
      resetTimeoutRef.current = window.setTimeout(() => {
        element.style.setProperty('--swipe-offset', '0px')
        resetTimeoutRef.current = null
      }, 16)
    }
    
    // Register touchmove with { passive: false } so we can call preventDefault() for horizontal swipes
    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })
    element.addEventListener('touchcancel', handleTouchEnd, { passive: true })
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
      element.removeEventListener('touchcancel', handleTouchEnd)
      
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current)
        resetTimeoutRef.current = null
      }
    }
  }, [threshold, velocityThreshold])

  return {
    swipeRef,
    isDragging,
  }
}

export default useSwipeGesture
